import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Layers, Settings2, Wand2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { toast } from "@/components/ui/toast";
import { Skeleton } from "@/components/ui/states";
import { fetchSpecFields, generateVariants, type ProductOption } from "./productApi";

interface Dimension {
  position: number;
  fieldId: string | null;
  name: string;
  values: string[];
}

const MAX_DIMENSIONES = 3;

/**
 * Atributos y variantes.
 *
 * La tienda define Color con ocho valores; este polo concreto solo se hace en
 * negro y azul. Aqui se elige ese subconjunto y se genera la matriz.
 *
 * Los atributos NO se crean desde aqui: son del catalogo entero y su numero
 * depende del plan contratado.
 */
export function VariantsPanel({
  productId,
  organizationId,
  categoryId,
  options,
  currency,
}: {
  productId: string;
  organizationId: string;
  categoryId: string | null;
  options: ProductOption[];
  currency: string;
}) {
  const qc = useQueryClient();

  const atributos = useQuery({
    queryKey: ["spec-fields", organizationId, categoryId],
    queryFn: () => fetchSpecFields(organizationId, categoryId),
  });

  // Solo sirven los que tienen lista cerrada de valores: no se puede generar
  // una matriz a partir de un campo de texto libre.
  const disponibles = useMemo(
    () => (atributos.data ?? []).filter((f) => f.options.length > 0),
    [atributos.data],
  );

  const [dimensiones, setDimensiones] = useState<Dimension[]>(() =>
    options
      .filter((o) => o.values.length > 0)
      .map((o) => ({
        position: o.position,
        fieldId: o.field_id,
        name: o.name,
        values: o.values,
      })),
  );

  const [precio, setPrecio] = useState("");
  const [busy, setBusy] = useState(false);

  const combinaciones = dimensiones.reduce(
    (n, d) => n * Math.max(d.values.length, 0),
    dimensiones.length > 0 ? 1 : 0,
  );

  const usados = new Set(dimensiones.map((d) => d.fieldId));
  const puedeAnadir = dimensiones.length < MAX_DIMENSIONES;

  function anadir(fieldId: string) {
    const f = disponibles.find((x) => x.id === fieldId);
    if (!f) return;
    setDimensiones((d) => [
      ...d,
      { position: d.length + 1, fieldId: f.id, name: f.label, values: [] },
    ]);
  }

  function quitar(position: number) {
    setDimensiones((d) =>
      d.filter((x) => x.position !== position).map((x, i) => ({ ...x, position: i + 1 })),
    );
  }

  function alternarValor(position: number, valor: string) {
    setDimensiones((d) =>
      d.map((x) =>
        x.position === position
          ? {
              ...x,
              values: x.values.includes(valor)
                ? x.values.filter((v) => v !== valor)
                : [...x.values, valor],
            }
          : x,
      ),
    );
  }

  async function generar() {
    setBusy(true);
    try {
      const r = await generateVariants(
        productId,
        dimensiones.map((d) => ({
          position: d.position,
          field_id: d.fieldId,
          name: d.name,
          values: d.values,
        })),
        precio.trim() || null,
      );

      const partes: string[] = [];
      if (r.created) partes.push(`${r.created} nueva${r.created === 1 ? "" : "s"}`);
      if (r.reactivated) partes.push(`${r.reactivated} reactivada${r.reactivated === 1 ? "" : "s"}`);
      if (r.deactivated) partes.push(`${r.deactivated} desactivada${r.deactivated === 1 ? "" : "s"}`);

      toast.ok(partes.length > 0 ? `Variantes: ${partes.join(", ")}` : "Sin cambios");
      await qc.invalidateQueries({ queryKey: ["product", productId] });
    } catch (e) {
      toast.error(friendlyError(e));
    }
    setBusy(false);
  }

  const incompleta = dimensiones.some((d) => d.values.length === 0);

  return (
    <Panel
      title="Atributos y variantes"
      actions={
        <Link
          to="/configuracion/atributos"
          className="flex items-center gap-1.5 text-meta text-accent hover:underline"
        >
          <Settings2 className="size-3.5" />
          Administrar
        </Link>
      }
    >
      {atributos.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8" />
          <Skeleton className="h-8" />
        </div>
      ) : disponibles.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          <Layers className="size-5 text-ink-3" />
          <p className="text-ui text-ink-2">No hay atributos con valores</p>
          <p className="max-w-[38ch] text-xs text-ink-3">
            Para generar variantes necesitas un atributo de selección con sus valores, como
            Color o Talla. Se configuran en Configuración → Atributos.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {dimensiones.map((d) => {
            const f = disponibles.find((x) => x.id === d.fieldId);
            return (
              <div key={d.position} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-baseline gap-2">
                    <span className="text-meta font-medium">{d.name}</span>
                    <span className="tnum text-xs text-ink-3">
                      {d.values.length} de {f?.options.length ?? 0}
                    </span>
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => quitar(d.position)}>
                    Quitar
                  </Button>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {(f?.options ?? []).map((v) => {
                    const on = d.values.includes(v);
                    return (
                      <button
                        key={v}
                        type="button"
                        aria-pressed={on}
                        onClick={() => alternarValor(d.position, v)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs transition-colors",
                          on
                            ? "border-accent bg-accent-soft text-accent"
                            : "border-line bg-surface text-ink-2 hover:bg-sunk",
                        )}
                      >
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {puedeAnadir && (
            <label className="flex flex-col gap-1.5">
              <span className="text-meta font-medium">
                {dimensiones.length === 0 ? "Primer atributo" : "Añadir atributo"}
              </span>
              <select
                value=""
                onChange={(e) => e.target.value && anadir(e.target.value)}
                className="w-full rounded-md border border-line bg-surface px-3 py-2 text-ui"
              >
                <option value="">Elegir…</option>
                {disponibles
                  .filter((f) => !usados.has(f.id))
                  .map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label} ({f.options.length} valores)
                    </option>
                  ))}
              </select>
              {dimensiones.length === MAX_DIMENSIONES - 1 && (
                <span className="text-xs text-ink-3">
                  Máximo tres: la matriz crece multiplicando.
                </span>
              )}
            </label>
          )}

          {dimensiones.length > 0 && (
            <div className="flex flex-col gap-3 border-t border-line-2 pt-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-meta font-medium">Precio de las nuevas</span>
                <span className="flex">
                  <span className="flex items-center rounded-l-md border border-r-0 border-line bg-sunk px-2.5 font-mono text-meta text-ink-3">
                    {currency === "PEN" ? "S/" : currency}
                  </span>
                  <input
                    value={precio}
                    onChange={(e) => setPrecio(e.target.value)}
                    inputMode="decimal"
                    placeholder="Igual que la más barata"
                    className="tnum w-full rounded-r-md border border-line bg-surface px-3 py-2 text-right text-ui"
                  />
                </span>
                <span className="text-xs text-ink-3">
                  Solo afecta a las que se creen ahora. Las existentes conservan su precio.
                </span>
              </label>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-ui text-ink-2">
                  {incompleta ? (
                    <span className="text-warn">Elige al menos un valor por atributo</span>
                  ) : (
                    <>
                      <span className="tnum font-semibold text-ink">{combinaciones}</span>{" "}
                      combinación{combinaciones === 1 ? "" : "es"}
                    </>
                  )}
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  loading={busy}
                  disabled={incompleta}
                  icon={<Wand2 className="size-3.5" />}
                  onClick={() => void generar()}
                >
                  Generar variantes
                </Button>
              </div>

              <p className="text-xs text-ink-3">
                Generar nunca borra. Las combinaciones que quites se desactivan: conservan su
                stock y sus pedidos, y vuelven tal cual si las seleccionas de nuevo.
              </p>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
